import { useMutation, useQueryClient, type InfiniteData } from '@tanstack/react-query'

import { commentApi } from '../api/commentApi'
import type { TCommentApi, TCommentCreatePayload, TCommentsApi } from '../api/types/commentApi.types'
import { getCommentsOptions } from './useCommentsQuery'

export const useCreateCommentMutation = (publicationId: number, payload: TCommentCreatePayload) => {
  const queryClient = useQueryClient()
  const initialPageParam = getCommentsOptions(publicationId).initialPageParam

  return useMutation({
    mutationFn: () => commentApi.createComment(payload),
    onSuccess: (comment: TCommentApi) => {
      queryClient.setQueryData(
        getCommentsOptions(publicationId).queryKey,
        (oldData): InfiniteData<TCommentsApi, typeof initialPageParam> | undefined => {
          const previousData = { ...oldData }
          const pageParamsForNewComment = {
            // The offset will be less than 0 because the next call for comments will be currentOffset + limit
            // and we must make sure that the new comment is not included in the next call
            offset: initialPageParam.offset - (initialPageParam.limit - 1),
            limit: initialPageParam.limit,
            expect: 1, // We expect only one comment because we just added one
          }
          const newPageParams = [pageParamsForNewComment]
          // If there are already comments in the cache, we need to update the last pageParams so the offset is correct
          // so it does not include comments already fetched
          if (previousData.pageParams) {
            // oxlint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- library boundary
            const previousPageParams = previousData.pageParams as (typeof initialPageParam)[]
            const lastPageParams = previousPageParams.pop()
            newPageParams.push(...previousPageParams)
            if (lastPageParams) {
              lastPageParams.offset = lastPageParams.offset + 1
              newPageParams.push(lastPageParams)
            }
          }

          const newPages = previousData?.pages ? [[comment], ...previousData.pages] : [[comment]]

          const newData = {
            pages: newPages,
            pageParams: newPageParams,
          }
          return newData
        },
      )
    },
  })
}
